import { Box, Stack, Theme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Image from '@/components/ui/Image';
import ImageCard from '@/components/ImageCard';
import CopyrightNotice from '@/components/CopyrightNotice';
import { SXStyleSheet } from '@/utils';

/**
 * Rahmen der Anmelde-Seiten (Login, Passwort vergessen, Passwort setzen).
 *
 * 🔥 SCROLLBAR, WENN DER PLATZ NICHT REICHT (Feedback 28.08.2026): bei 130 %
 * Browser-Zoom war der Login-Knopf nicht erreichbar. Der `ScreenWrapper`
 * darueber ist `overflow: hidden` mit voller Fensterhoehe, und die Spalte war
 * senkrecht ZENTRIERT — ragt zentrierter Inhalt ueber den Rand, schneidet der
 * Browser ihn oben UND unten ab, und es gibt keinen Weg dorthin.
 *
 * Deshalb hier (und nur hier — der ScreenWrapper traegt auch die Shell, die
 * ihr eigenes Scrollen hat):
 *   - der Rahmen selbst scrollt (`overflowY: auto`),
 *   - die Innenflaeche ist mindestens so hoch wie das Fenster, waechst aber
 *     mit dem Inhalt (`flex: 1 0 auto`) — sie wird nie kleiner als ihr Inhalt,
 *   - zentriert wird ueber `margin: auto` am Inhaltsblock statt
 *     `justify-content: center`: bei Platzmangel faellt der Rand auf 0 und der
 *     Block beginnt oben, statt nach oben aus dem Bild zu ragen,
 *   - der Abstand Logo/Formular schrumpft mit der Fensterhoehe,
 *   - das Bild rechts entfaellt auf schmalen Fenstern (unter `md`).
 */
const AuthView = () => {
  const { t } = useTranslation();

  return (
    <Stack flex={1} minHeight={0} sx={style.container} data-testid="auth-rahmen">
      <Stack flexDirection="row" sx={style.innen} data-testid="auth-innen">
        <Stack flex={1} minWidth={0}>
          <Stack
            pt="25px"
            px="16px"
            alignItems="center"
            sx={style.inhalt}
            data-testid="auth-inhalt"
          >
            <Image image="Logo" width="351px" maxWidth="100%" />
            <Stack width="363px" maxWidth="100%">
              <Outlet />
            </Stack>
          </Stack>
          <CopyrightNotice />
        </Stack>
        <Box sx={style.bild}>
          <ImageCard
            image="Insurance"
            title={t('auth:side.title')}
            subtitle={t('auth:side.subtitle')}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const style = SXStyleSheet.create({
  container: {
    overflowY: 'auto',
    background: (theme: Theme) =>
      `linear-gradient(301deg, ${theme.palette.common.white} 42.63%, #A5ADC0 161.1%, rgba(0, 0, 0, 0.50) 289.44%), ${theme.palette.common.white}`,
  },
  innen: {
    // Mindestens Fensterhoehe, sonst Inhaltshoehe — nie kleiner als der Inhalt.
    flex: '1 0 auto',
  },
  inhalt: {
    // Vertikal mittig, solange Platz ist; bei Platzmangel oben bündig.
    my: 'auto',
    // 89px auf grossen Fenstern, bei wenig Hoehe (Zoom) bis 32px herunter.
    gap: 'clamp(32px, 8vh, 89px)',
  },
  bild: {
    display: { xs: 'none', md: 'flex' },
    flex: 1,
  },
});

export default AuthView;
